/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { GlobalStateService } from './global.service.js';
import { interpolateNaively } from '../utils/string.js';
import { mergeObjects, objectNaiveDeepEquals } from '../utils/identity.js';
export const componentStringsDefault = {
    actions: {
        sort: 'Sort',
        expand: 'Expand',
        close: 'Close',
        resize: 'Resize',
        filter: 'Filter',
    },
    alert: {
        closeButtonAriaLabel: 'Close',
        loading: 'Loading',
        info: 'Info',
        success: 'Success',
        warning: 'Warning',
        danger: 'Error',
    },
    dropdown: {
        open: 'Open',
    },
    file: {
        browse: 'browse',
        files: 'files',
        removeFile: 'remove file',
    },
    modal: {
        closeButtonAriaLabel: 'Close modal',
        contentStart: 'Beginning of modal content',
        contentBox: 'Scrollable modal body',
        contentEnd: 'End of modal content',
    },
    navigation: {
        navigationElement: 'navigation',
        navigationLabel: 'navigation menu',
        navigationAbridgedText: 'View abridged menu',
        navigationUnabridgedText: 'View unabridged menu',
    },
    overlay: {
        closeButtonAriaLabel: 'Close dialog',
        contentStart: 'Beginning of dialog content',
        contentEnd: 'End of dialog content',
    },
    popup: {
        closeButtonAriaLabel: 'Close popup',
        contentStart: 'Beginning of popup content',
        contentEnd: 'End of popup content',
    },
    password: {
        showButtonAriaLabel: 'Show password',
        hideButtonAriaLabel: 'Hide password',
    },
    progress: {
        loading: 'Loading',
        looping: 'Loading',
    },
    treeview: {
        loading: 'Loading',
    },
    grid: {
        resizeColumn: 'Resize Column',
        closeDetails: 'Close Details',
        noData: 'No Results Found',
        rowDetailStart: 'Start of row details',
        rowDetailEnd: 'End of row details',
        footerEnd: 'End of grid rows',
        action: 'Action',
        dropTarget: 'Drop Item',
        pagination: {
            label: 'grid pagination',
            firstPage: 'go to first page',
            previousPage: 'go to previous page',
            nextPage: 'go to next page',
            lastPage: 'go to last page',
            pageSize: 'rows per page',
            page: 'page',
        },
    },
};
/**
 * I18nService is a static class that gives users the ability to use and override
 * strings within the components for internationalization / globalization. One
 * can override default values globally for their application or override per
 * component instance as needed.
 *
 * Use the localize method to override values globally. For per component instance,
 * use the i18n decorator.
 */
export class I18nService {
    static get keys() {
        // intialize registry if not yet set
        if (Object.keys(GlobalStateService.state.i18nRegistry).length === 0) {
            GlobalStateService.state.i18nRegistry = mergeObjects(componentStringsDefault, GlobalStateService.state.i18nRegistry);
        }
        return mergeObjects(componentStringsDefault, GlobalStateService.state.i18nRegistry);
    }
    static findKey(keyValueObj) {
        const keyVals = I18nService.keys;
        return Object.keys(keyVals).find(key => objectNaiveDeepEquals(keyVals[key], keyValueObj));
    }
    static get(key) {
        if (!key) {
            return {};
        }
        return I18nService.keys[key] || {};
    }
    /** @private */
    static reset() {
        // largely useful for unit testing
        GlobalStateService.state.i18nRegistry = mergeObjects({}, componentStringsDefault);
    }
    static hydrate(i18nObject, componentInstance) {
        return JSON.parse(interpolateNaively(JSON.stringify(i18nObject), componentInstance));
    }
    static localize(overrides) {
        GlobalStateService.state.i18nRegistry = mergeObjects(componentStringsDefault, GlobalStateService.state.i18nRegistry, overrides);
    }
}
